import * as Types from '@trello/graphql/generated';

import { useQuickLoad } from '@trello/quickload';
import type { TrelloQueryHookOptions, TrelloLazyQueryHookOptions, TrelloSuspenseQueryHookOptions } from '@trello/quickload';
import DocumentNode from '@trello/graphql/src/documentNode';
import * as Apollo from '@apollo/client';
export const ViewSwitcherBoardDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"ViewSwitcherBoard"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"boardId"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"ID"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"board"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"id"},"value":{"kind":"Variable","name":{"kind":"Name","value":"boardId"}}}],"directives":[{"kind":"Directive","name":{"kind":"Name","value":"client"}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"closed"}},{"kind":"Field","name":{"kind":"Name","value":"enterpriseOwned"}},{"kind":"Field","name":{"kind":"Name","value":"idEnterprise"}},{"kind":"Field","name":{"kind":"Name","value":"idOrganization"}},{"kind":"Field","name":{"kind":"Name","value":"memberships"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"deactivated"}},{"kind":"Field","name":{"kind":"Name","value":"idMember"}},{"kind":"Field","name":{"kind":"Name","value":"memberType"}},{"kind":"Field","name":{"kind":"Name","value":"orgMemberType"}},{"kind":"Field","name":{"kind":"Name","value":"unconfirmed"}}]}},{"kind":"Field","name":{"kind":"Name","value":"organization"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"memberships"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"deactivated"}},{"kind":"Field","name":{"kind":"Name","value":"idMember"}},{"kind":"Field","name":{"kind":"Name","value":"memberType"}},{"kind":"Field","name":{"kind":"Name","value":"unconfirmed"}}]}},{"kind":"Field","name":{"kind":"Name","value":"offering"}}]}},{"kind":"Field","name":{"kind":"Name","value":"prefs"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"isTemplate"}},{"kind":"Field","name":{"kind":"Name","value":"selfJoin"}},{"kind":"Field","name":{"kind":"Name","value":"switcherViews"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"enabled"}},{"kind":"Field","name":{"kind":"Name","value":"viewType"}}]}}]}},{"kind":"Field","name":{"kind":"Name","value":"premiumFeatures"}}]}}]}}]} as unknown as DocumentNode;
const defaultOptions = {"context":{"operationName":"ViewSwitcherBoard","document":ViewSwitcherBoardDocument}} as const;
export type ViewSwitcherBoardQueryVariables = Types.Exact<{
  boardId: Types.Scalars['ID']['input'];
}>;


export type ViewSwitcherBoardQuery = (
  { __typename: 'Query' }
  & { board?: Types.Maybe<(
    { __typename: 'Board' }
    & Pick<Types.Board, 'id' | 'closed' | 'enterpriseOwned' | 'idEnterprise' | 'idOrganization' | 'premiumFeatures'>
    & { memberships: Array<(
      { __typename: 'Board_Membership' }
      & Pick<Types.Board_Membership, 'id' | 'deactivated' | 'idMember' | 'memberType' | 'orgMemberType' | 'unconfirmed'>
    )>, organization?: Types.Maybe<(
      { __typename: 'Organization' }
      & Pick<Types.Organization, 'id' | 'offering'>
      & { memberships: Array<(
        { __typename: 'Organization_Membership' }
        & Pick<Types.Organization_Membership, 'id' | 'deactivated' | 'idMember' | 'memberType' | 'unconfirmed'>
      )> }
    )>, prefs?: Types.Maybe<(
      { __typename: 'Board_Prefs' }
      & Pick<Types.Board_Prefs, 'isTemplate' | 'selfJoin'>
      & { switcherViews: Array<(
        { __typename: 'Board_Prefs_SwitcherView' }
        & Pick<Types.Board_Prefs_SwitcherView, 'enabled' | 'viewType'>
      )> }
    )> }
  )> }
);

/**
 * __useViewSwitcherBoardQuery__
 *
 * To run a query within a React component, call `useViewSwitcherBoardQuery` and pass it any options that fit your needs.
 * When your component renders, `useViewSwitcherBoardQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useViewSwitcherBoardQuery({
 *   variables: {
 *      boardId: // value for 'boardId'
 *   },
 * });
 */
export function useViewSwitcherBoardQuery(
  baseOptions: TrelloQueryHookOptions<
    ViewSwitcherBoardQuery,
    ViewSwitcherBoardQueryVariables
  > &
    (
      | { variables: ViewSwitcherBoardQueryVariables; skip?: boolean }
      | { skip: boolean }
    ),
) {
  const isQuickLoading = useQuickLoad({
    waitOn: baseOptions?.waitOn || ['None'],
    document: ViewSwitcherBoardDocument,
    skip: baseOptions?.skip,
  });
  const options = {
    ...defaultOptions,
    ...baseOptions,
    skip: isQuickLoading || baseOptions?.skip,
  };
  const result = Apollo.useQuery<
    ViewSwitcherBoardQuery,
    ViewSwitcherBoardQueryVariables
  >(ViewSwitcherBoardDocument, options);
  // reconstruct result because modifying the useQuery result actually changes apollo behavior because of memoization
  const trelloResult = { ...result };
  if (isQuickLoading) {
    trelloResult.loading = true;
  }

  return trelloResult;
}
export function useViewSwitcherBoardLazyQuery(
  baseOptions?: TrelloLazyQueryHookOptions<
    ViewSwitcherBoardQuery,
    ViewSwitcherBoardQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<
    ViewSwitcherBoardQuery,
    ViewSwitcherBoardQueryVariables
  >(ViewSwitcherBoardDocument, options);
}
export function useViewSwitcherBoardSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | TrelloSuspenseQueryHookOptions<
        ViewSwitcherBoardQuery,
        ViewSwitcherBoardQueryVariables
      >,
) {
  const options =
    baseOptions === Apollo.skipToken
      ? baseOptions
      : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<
    ViewSwitcherBoardQuery,
    ViewSwitcherBoardQueryVariables
  >(ViewSwitcherBoardDocument, options);
}
export type ViewSwitcherBoardQueryHookResult = ReturnType<
  typeof useViewSwitcherBoardQuery
>;
export type ViewSwitcherBoardLazyQueryHookResult = ReturnType<
  typeof useViewSwitcherBoardLazyQuery
>;
export type ViewSwitcherBoardSuspenseQueryHookResult = ReturnType<
  typeof useViewSwitcherBoardSuspenseQuery
>;
export type ViewSwitcherBoardQueryResult = Apollo.QueryResult<
  ViewSwitcherBoardQuery,
  ViewSwitcherBoardQueryVariables
>;
